import { memo, useCallback, useEffect, useState } from 'react';
import { Platform } from 'react-native';
import {
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import { GameThumbnail, launchGame } from '@bonanza-social/games';
import {
  BottomSheet,
  useNavigation,
  StackNavigationProp,
  resetToRootNavigator,
} from '@bonanza-social/navigation';
import {
  useDispatch,
  useSelector,
  selectTokenName,
  selectTokenIconLarge,
} from '@bonanza-social/toolkit/state';
import {
  Box,
  Text,
  ParallaxList,
  Image,
  Icon,
  Button,
  TokensIcon,
} from '@bonanza-social/moonlight';
import { RewardReceivedBottomSheetProps } from './RewardReceivedBottomSheet.types';
import { NotificationReward, RewardType } from '../../state/rewards.types';
import { formatRewardAmount } from './RewardReceivedBottomSheet.utils';
import {
  DEFAULT_ANIMATION_DURATION,
  TEXT_ANIMATION_DURATION,
  IMAGE_COMMON_PROPS,
  REWARD_BUTTON_TEXTS,
  REWARD_ICONS,
  REWARD_IMAGES,
  REWARD_TEXTS,
  DEFAULT_ANIMATION_DELAY,
} from './RewardReceivedBottomSheet.content';
import QuestCompleted from './QuestCompleted/QuestCompleted';
import { useInstantMetaGame } from '@bonanza-social/meta-games';

function RewardReceivedBottomSheet({
  rewards,
  questCompleted,
  close,
}: RewardReceivedBottomSheetProps) {
  const [activeIndex, setActiveIndex] = useState(Number(rewards.length > 2));
  const dispatch = useDispatch();
  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();
  const tokenName = useSelector(selectTokenName);
  const tokenIcon = useSelector(selectTokenIconLarge);
  const animatedValue = useSharedValue(Number(!questCompleted));
  const textAnimatedValue = useSharedValue(1);
  const activeReward = rewards[activeIndex];
  const { rewardType, gameIds } = activeReward;
  const { launchMetaGame: launchMetaWheel } = useInstantMetaGame('Wheel');
  const { launchMetaGame: launchMetaScratcher } =
    useInstantMetaGame('Scratcher');

  const renderItem = useCallback(
    ({ rewardType, gameIds }: NotificationReward) => {
      const [gameId] = gameIds || [];

      if (gameId !== undefined) {
        return (
          <Box width={101} height={101} marginVertical="auto">
            <GameThumbnail
              gameId={gameId}
              type="square"
              borderRadius="radius-400"
              overflow="hidden"
              dynamicSize
              {...IMAGE_COMMON_PROPS}
            />
          </Box>
        );
      }

      if (rewardType === RewardType.FreeSpins) {
        return null;
      }

      if (rewardType === RewardType.Tokens) {
        return <TokensIcon {...tokenIcon} {...IMAGE_COMMON_PROPS} />;
      }

      return <Image name={REWARD_IMAGES[rewardType]} {...IMAGE_COMMON_PROPS} />;
    },
    [tokenIcon],
  );

  const onButtonPress = useCallback(() => {
    close();

    if (rewardType === RewardType.Scratcher) {
      launchMetaScratcher();
    }

    if (rewardType === RewardType.Wheel) {
      launchMetaWheel();
    }

    if (rewardType === RewardType.Coins) {
      resetToRootNavigator(() => {
        navigation.navigate('PlayWithCoinsScreen');
      });
    }

    if (rewardType === RewardType.FreeSpins && gameIds) {
      resetToRootNavigator(() => {
        dispatch(launchGame({ id: gameIds[0] }));
      });
    }

    if (rewardType === RewardType.InstantWheel) {
      // TODO: check types
      BottomSheet.open({
        namespace: 'instantWheel',
        type: 'InstantWheel',
      });
    }
  }, [
    rewardType,
    gameIds,
    navigation,
    dispatch,
    close,
    launchMetaScratcher,
    launchMetaWheel,
  ]);

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: animatedValue.value,
  }));

  const textAnimatedStyle = useAnimatedStyle(() => ({
    opacity: textAnimatedValue.value,
    transform: [
      {
        scale: textAnimatedValue.value,
      },
    ],
  }));

  useEffect(() => {
    textAnimatedValue.value = 0;
    textAnimatedValue.value = withSpring(1, {
      duration: TEXT_ANIMATION_DURATION,
    });
  }, [activeIndex, textAnimatedValue]);

  useEffect(() => {
    if (questCompleted) {
      animatedValue.value = withDelay(
        DEFAULT_ANIMATION_DELAY + 2 * DEFAULT_ANIMATION_DURATION,
        withTiming(1, {
          duration: DEFAULT_ANIMATION_DURATION,
        }),
      );
    }
  }, [questCompleted, animatedValue]);

  const rewardIcon =
    rewardType === RewardType.Tokens ? (
      <TokensIcon {...tokenIcon} width="space-400" height="space-400" />
    ) : (
      <Icon
        name={REWARD_ICONS[rewardType]}
        color="iconPrimary"
        width="space-400"
        height="space-400"
      />
    );

  return (
    <>
      <Box animatedStyle={animatedStyle}>
        <Text
          variant="overline"
          locNS="rewards"
          locKey="yourReward"
          textAlign="center"
          paddingTop="space-400"
          paddingBottom="space-200"
          color="accentSurfacePrimary"
        />
        <Text
          variant="h3"
          textAlign="center"
          color="accentSurfacePrimary"
          animatedStyle={textAnimatedStyle}
        >
          {formatRewardAmount(activeReward)}
        </Text>
        <Box
          height="space-1000"
          borderWidth="border-width-025"
          borderLeftWidth="border-width-0"
          borderRightWidth="border-width-0"
          borderColor="borderPrimary"
          marginVertical="space-400"
          paddingVertical="space-300"
        >
          <Box
            gap="space-300"
            alignItems="center"
            justifyContent="center"
            flexDirection="row"
            animatedStyle={textAnimatedStyle}
          >
            {rewardIcon}
            <Text
              variant="callout"
              locNS="rewards"
              locKey={REWARD_TEXTS[rewardType]}
              color="accentSurfacePrimary"
              textAlign="center"
            >
              {rewardType === RewardType.Tokens ? tokenName : undefined}
            </Text>
            {rewardIcon}
          </Box>
        </Box>
        <ParallaxList
          data={rewards}
          activeIndex={activeIndex}
          renderItem={renderItem}
          onChangeActiveIndex={setActiveIndex}
          height={Platform.select({
            ios: 220,
            android: 220,
            web: 230,
          })}
        />
        <Button
          onPress={onButtonPress}
          locKey={REWARD_BUTTON_TEXTS[rewardType]}
          locNS="rewards"
          marginTop="space-400"
          size="large"
          light
        />
      </Box>
      {questCompleted && <QuestCompleted />}
    </>
  );
}

export default memo(RewardReceivedBottomSheet);
